import React, { useState, useEffect, useRef } from 'react';
import { Star, Quote, MapPin, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';

const testimonials = [
  {
    id: 1,
    name: "CHARLE WEMA",
    role: "Particulier",
    avatar: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1773001351/4_w0l3nr.jpg",
    location: "Polynésie",
    text: "Une transparence totale sur l'état du véhicule et les coûts d'importation. Mon Audi est arrivée au port exactement comme décrite. Économie réelle et service pro.",
    stars: 5,
    car: "Audi Q5",
    tags: ["Import Direct", "Confiance"]
  },
  {
    id: 2,
    name: "GEGE TAMBOUEON",
    role: "Passionné",
    avatar: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1773001351/3_pk9bin.jpg",
    location: "Nouvelle-Calédonie",
    text: "Je cherchais une configuration spécifique introuvable localement. L'équipe l'a sourcée et livrée en un temps record. Une gestion logistique irréprochable.",
    stars: 5,
    car: "VW Golf 8 GTI",
    tags: ["Sourcing Rare"]
  },
  {
    id: 3,
    name: "PASCAL DAYE & FAMILLE",
    role: "Famille",
    avatar: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1773001351/2_kwp335.jpg",
    location: "Polynésie",
    text: "L'accompagnement idéal pour notre projet familial. Le bateau est arrivé prêt pour la mise à l'eau. Expertise vidéo et suivi WhatsApp constant, très rassurant.",
    stars: 5,
    car: "Cap Camarat 7.5 WA",
    tags: ["Projet Familial", "Clé en main"]
  },
  {
    id: 4,
    name: "VARINIA & MONIA",
    role: "Professionnelles",
    avatar: "https://res.cloudinary.com/ds9vdtt8l/image/upload/v1773001351/1_rg9tiu.jpg",
    location: "Polynésie",
    text: "Une solution efficace pour le renouvellement de notre flotte. Formalités douanières et transport gérés de A à Z. On gagne du temps et de la sérénité.",
    stars: 5,
    car: "Parc Automobile",
    tags: ["Expertise Pro", "Logistique"]
  }
];

export default function Testimonials() {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 400;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  return (
    <section className="relative bg-white pt-24 pb-20 overflow-hidden">
      {/* TRANSITION FLUIDE : 
         Ce bloc crée un dégradé qui part du noir du carousel (en haut) 
         pour arriver au blanc de cette section de façon invisible.
      */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-[#0a0d12] to-white pointer-events-none" />

      <div className="max-w-[1600px] mx-auto px-6 md:px-10 relative">

        {/* Header Epuré */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
          <div className="space-y-4">
            <h2 className="text-4xl md:text-5xl font-black italic uppercase tracking-tighter text-gray-900 leading-none">
              RETOURS <span className="text-red-600 underline decoration-red-600/20 underline-offset-8">EXPÉRIENCE</span>
            </h2>
            <div className="flex items-center gap-3">
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} size={14} className="fill-red-600 text-red-600" />)}
              </div>
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-400">Note 4.9/5 par nos clients</span>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className={`p-4 rounded-full border transition-all ${canScrollLeft ? 'border-gray-200 text-gray-900 hover:bg-gray-50' : 'border-gray-100 text-gray-200 cursor-not-allowed'}`}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className={`p-4 rounded-full border transition-all ${canScrollRight ? 'border-gray-200 text-gray-900 hover:bg-gray-50' : 'border-gray-100 text-gray-200 cursor-not-allowed'}`}
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        {/* Grille de cartes */}
        <div 
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex gap-8 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-6"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {testimonials.map((item) => (
            <div
              key={item.id}
              className="snap-start shrink-0 w-[85vw] sm:w-[420px] bg-gray-50/50 border border-gray-100 p-8 flex flex-col relative group hover:bg-white hover:shadow-2xl hover:shadow-gray-200/50 transition-all duration-500"
            >
              <Quote size={50} className="text-gray-100 absolute top-8 right-8 group-hover:text-red-600/5 transition-colors" />

              <div className="flex items-center gap-5 mb-8">
                <div className="relative">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-16 h-16 rounded-full object-cover shadow-lg group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute -bottom-1 -right-1 bg-white p-1 rounded-full shadow-sm">
                    <CheckCircle size={14} className="text-red-600 fill-red-50" />
                  </div>
                </div>
                <div>
                  <h3 className="text-sm font-black italic uppercase text-gray-900 tracking-wider leading-none">{item.name}</h3>
                  <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">
                    <MapPin size={10} className="text-red-600" /> {item.location}
                  </div>
                </div>
              </div>

              <p className="text-gray-600 text-[15px] leading-relaxed font-medium italic mb-8 flex-1">
                "{item.text}"
              </p>

              <div className="pt-6 border-t border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">{item.car}</span>
                  <span className="text-[9px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded uppercase">Vérifié</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {item.tags.map(tag => (
                    <span key={tag} className="text-[9px] font-bold text-gray-400 bg-white border border-gray-100 px-2 py-1 uppercase tracking-tighter">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
